import { message } from "antd";
import {
  Dashboard,
  UserFormValues,
  User,
  DashboardOption,
} from "src/layouts/UserManagement/UserInterfaces";
import { getApiBaseUrl } from "src/config/endpoints";
import axios from "axios";

const API_BASE_URL = getApiBaseUrl();

//*****************User Management Screen ******************//
export const fetchUsers = async (api_key: string, tenant_id: string) => {
  const response = await fetch(`${API_BASE_URL}/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api_key}`,
    },
    body: JSON.stringify({ tenant_id }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch users");
  }

  const data = await response.json();
  const usersPayload = Array.isArray(data)
    ? data
    : data.users ?? data.data ?? [];
  const socialListeningEnabled =
    typeof data?.social_listening_access === "boolean"
      ? data.social_listening_access
      : true;

  const users = usersPayload.map((user: any) => ({
    id: user.user_id,
    name: user.username,
    email: user.email,
    accessLevel: user.role_name,
    isActive: user.is_active,
    first_name: user.first_name,
    last_name: user.last_name,
    last_login: user.last_login,
    socialListeningAccess:
      typeof user.social_listening_access === "boolean"
        ? user.social_listening_access
        : false,
  }));

  return { users, socialListeningEnabled };
};

export const updateUserStatus = async (
  api_key: string,
  user_id: number,
  is_active: boolean,
  role_name: string,
  tenant_id: string
) => {
  const response = await fetch(`${API_BASE_URL}/update-user-status`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api_key}`,
    },
    body: JSON.stringify({ user_id, is_active, role_name, tenant_id }),
  });

  if (response.ok) {
    message.success("User updated successfully");
  }

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to update user");
  }
};

export const deleteUser = async (
  api_key: string,
  user_id: number,
  tenant_id: string
) => {
  const response = await fetch(`${API_BASE_URL}/delete-user`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api_key}`,
    },
    body: JSON.stringify({ user_id, tenant_id }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to delete user");
  }
};

//*****************Add User Modal ******************//
export const fetchDashboards = async (
  tenant_id: string,
  api_key: string
): Promise<{ name: string; value: string }[]> => {
  const response = await axios.post(
    `${API_BASE_URL}/tenant/dashboards`,
    { tenant_id },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${api_key}`,
      },
    }
  );

  const dashboards = response.data.map((dashboard: Dashboard) => ({
    name: dashboard.dashboard_name,
    value: dashboard.dashboard_id.toString(),
  }));

  return dashboards;
};

export const addUserAPI = async (
  formValues: UserFormValues,
  selectedDashboards: string[],
  socialListeningAccess: boolean,
  tenant_id: string,
  api_key: string
): Promise<User> => {
  const roleMapping: { [key: string]: number } = {
    viewer: 2,
  };

  const payload = {
    tenant_id,
    first_name: formValues.firstName,
    last_name: formValues.lastName,
    user_name: formValues.username,
    email: formValues.email,
    role_name: formValues.role,
    role_id: roleMapping[formValues.role],
    dashboard_access: selectedDashboards,
    social_listening_access: socialListeningAccess,
  };

  const response = await axios.post(`${API_BASE_URL}/add-user`, payload, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api_key}`,
    },
  });

  return {
    id: response.data.user_id,
    name: `${formValues.firstName} ${formValues.lastName}`,
    email: formValues.email,
    first_name: formValues.firstName,
    last_name: formValues.lastName,
    accessLevel: formValues.role,
    isActive: false,
    dashboardAccess: selectedDashboards,
    last_login: null,
    socialListeningAccess,
  };
};

//*****************Edit User Modal ******************//

export const fetchUserDashboards = async (
  api_key: string,
  tenant_id: string,
  user_id: number
): Promise<DashboardOption[]> => {
  const response = await fetch(`${API_BASE_URL}/users/dashboards`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api_key}`,
    },
    body: JSON.stringify({ tenant_id, user_id }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("unauthorized");
    }
    throw new Error("Failed to fetch dashboards");
  }

  return response.json();
};

export const updateUser = async (
  api_key: string,
  user_id: number,
  first_name: string,
  last_name: string,
  role_name: string,
  is_active: boolean,
  dashboard_access: number[],
  social_listening_access: boolean,
  tenant_id: string
) => {
  const response = await fetch(`${API_BASE_URL}/update-user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api_key}`,
    },
    body: JSON.stringify({
      user_id,
      first_name,
      last_name,
      role_name,
      is_active,
      dashboard_access,
      social_listening_access,
      tenant_id,
    }),
  });

  return response;
};

//***************** Dashboard Activity Logs ******************//
export const logDashboardViewAPI = async (
  api_key: string,
  user_id: number,
  dashboardName: string
): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/user-activity-logs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${api_key}`,
      },
      body: JSON.stringify({
        user_id,
        event: "Dashboard Viewed",
        details: `Viewed ${dashboardName}`,
      }),
    });
  } catch (error) {
    console.error("Failed to log dashboard view:", error);
  }
};
